/**
 * EditBannerDialog
 * Allows admins to pick a gradient preset for an institution banner.
 * Changes are saved to localStorage and applied immediately.
 */

import { useState } from "react";
import { Check, Palette, ImagePlus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { GRADIENT_PRESETS } from "../config/institutions";

interface EditBannerDialogProps {
  isOpen: boolean;
  onClose: () => void;
  currentGradient: string;
  onSave: (gradient: string) => void;
}

export function EditBannerDialog({
  isOpen,
  onClose,
  currentGradient,
  onSave,
}: EditBannerDialogProps) {
  const [selected, setSelected] = useState(currentGradient);

  const handleSave = () => {
    onSave(selected);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Palette className="size-4" />
            Personalizar Banner
          </DialogTitle>
          <DialogDescription>
            Elige el color del banner para esta institución. Los cambios se guardan localmente.
          </DialogDescription>
        </DialogHeader>

        {/* Color swatches */}
        <div className="space-y-4">
          <p className="text-sm font-medium text-muted-foreground">Color del banner</p>
          <div className="grid grid-cols-6 gap-3">
            {GRADIENT_PRESETS.map((preset) => (
              <button
                key={preset.value}
                title={preset.label}
                onClick={() => setSelected(preset.value)}
                className={cn(
                  "relative size-10 rounded-full transition-all duration-200",
                  "ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                  selected === preset.value
                    ? "scale-110 ring-2 ring-foreground ring-offset-2"
                    : "hover:scale-105"
                )}
                style={{ background: preset.value }}
              >
                {selected === preset.value && (
                  <Check className="absolute inset-0 m-auto size-4 text-white drop-shadow-md" />
                )}
              </button>
            ))}
          </div>

          {/* Preview */}
          <div
            className="h-16 w-full rounded-xl shadow-inner transition-all duration-300"
            style={{ background: selected }}
          />
        </div>

        {/* Logo: coming soon */}
        <div className="flex items-center gap-3 rounded-lg border border-dashed border-muted-foreground/30 p-3 text-muted-foreground">
          <ImagePlus className="size-5 shrink-0" />
          <div>
            <p className="text-sm font-medium">Logo personalizado</p>
            <p className="text-xs">Disponible próximamente</p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={handleSave}>Guardar cambios</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
